/**
 * 🌿 森の生態系コンテキスト
 * DAO全体の状態とメタファーに基づく体験を管理
 */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useResponsive } from '../hooks/useResponsive';

const ForestContext = createContext();

// DAO成長段階の定義
export const GROWTH_STAGES = {
  SEED: 'seed', // 🌱 種（問いの芽生え）
  SPROUT: 'sprout', // 🌿 新芽（初回共鳴）
  YOUNG_TREE: 'young', // 🌳 若木（活発な議論）
  MATURE_TREE: 'mature', // 🌲 成木（成熟した合意）
  FOREST: 'forest', // 🌳🌲🌿 森（エコシステム形成）
};

// 時間帯による森の状態
export const FOREST_TIME_STATES = {
  DAWN: { time: [5, 9], mood: 'fresh', theme: 'light' }, // 🌅 夜明け - 新鮮
  DAY: { time: [9, 17], mood: 'active', theme: 'bright' }, // ☀️ 昼 - 活発
  DUSK: { time: [17, 21], mood: 'warm', theme: 'golden' }, // 🌆 夕暮れ - 温か
  NIGHT: { time: [21, 5], mood: 'quiet', theme: 'dark' }, // 🌙 夜 - 静寂
};

// アクセシビリティ設定
export const ACCESSIBILITY_OPTIONS = {
  textSize: { small: 0.9, medium: 1.0, large: 1.2, xlarge: 1.4 },
  contrast: { normal: 'normal', high: 'high', dark: 'dark' },
  motion: { full: 'full', reduced: 'reduced', none: 'none' },
  haptic: { on: true, off: false },
};

export function ForestProvider({ children }) {
  // === 基本状態 ===
  const [forestState, setForestState] = useState({
    currentSeason: 'spring', // 春夏秋冬
    weatherMood: 'sunny', // 天気による雰囲気
    timeState: null, // 時間帯状態
    userGrowthStage: GROWTH_STAGES.SEED,
    isFirstVisit: true,
    lastActivity: null,
  });

  // === アクセシビリティ設定 ===
  const [accessibilitySettings, setAccessibilitySettings] = useState({
    textSize: ACCESSIBILITY_OPTIONS.textSize.medium,
    contrast: ACCESSIBILITY_OPTIONS.contrast.normal,
    motion: ACCESSIBILITY_OPTIONS.motion.full,
    haptic: ACCESSIBILITY_OPTIONS.haptic.on,
    screenReader: false,
  });

  // === 認知負荷軽減設定 ===
  const [cognitiveSettings, setCognitiveSettings] = useState({
    showOnboarding: true, // 初回説明表示
    enableHints: true, // ヒント表示
    simplifiedView: false, // 簡易表示モード
    autoProgress: true, // 自動進行
    explanationLevel: 'detailed', // 説明レベル
  });

  // === コンテキスト認識 ===
  const { isMobile, isTablet, isDesktop, isTouchDevice } = useResponsive();

  const [contextualData, setContextualData] = useState({
    deviceType: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop',
    connectionSpeed: 'fast', // slow, medium, fast
    batteryLevel: 100, // 0-100
    isOffline: false,
    location: null,
    preferredInteraction: isTouchDevice ? 'touch' : 'click',
  });

  // === 時間帯による状態更新 ===
  useEffect(() => {
    const updateTimeState = () => {
      const hour = new Date().getHours();
      const currentTimeState = Object.entries(FOREST_TIME_STATES).find(
        ([_, state]) => {
          const [start, end] = state.time;
          if (start <= end) {
            return hour >= start && hour < end;
          } else {
            // 夜をまたぐ場合（21-5時）
            return hour >= start || hour < end;
          }
        }
      );

      setForestState((prev) => ({
        ...prev,
        timeState: currentTimeState
          ? currentTimeState[1]
          : FOREST_TIME_STATES.DAY,
      }));
    };

    updateTimeState();
    const interval = setInterval(updateTimeState, 60000); // 1分ごと更新
    return () => clearInterval(interval);
  }, []);

  // === デバイス情報更新 ===
  useEffect(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return;
    }

    const updateDeviceContext = () => {
      try {
        // バッテリー情報取得（対応ブラウザのみ）
        if (
          navigator.getBattery &&
          typeof navigator.getBattery === 'function'
        ) {
          navigator
            .getBattery()
            .then((battery) => {
              if (battery && typeof battery.level === 'number') {
                setContextualData((prev) => ({
                  ...prev,
                  batteryLevel: Math.round(battery.level * 100),
                }));
              }
            })
            .catch(() => {
              // バッテリー API エラーは無視
            });
        }

        // 接続速度推定
        if (navigator.connection && navigator.connection.effectiveType) {
          const connection = navigator.connection;
          let speed = 'fast';
          if (connection.effectiveType === '2g') speed = 'slow';
          else if (connection.effectiveType === '3g') speed = 'medium';

          setContextualData((prev) => ({
            ...prev,
            connectionSpeed: speed,
          }));
        }
      } catch (error) {
        console.warn('⚠️ デバイス情報取得エラー:', error);
      }

      // オフライン状態監視
      const handleOnline = () =>
        setContextualData((prev) => ({ ...prev, isOffline: false }));
      const handleOffline = () =>
        setContextualData((prev) => ({ ...prev, isOffline: true }));

      if (
        window.addEventListener &&
        typeof window.addEventListener === 'function'
      ) {
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
          window.removeEventListener('online', handleOnline);
          window.removeEventListener('offline', handleOffline);
        };
      }
    };

    const cleanup = updateDeviceContext();
    return cleanup;
  }, []);

  // === 成長段階更新 ===
  const updateGrowthStage = useCallback((newStage) => {
    setForestState((prev) => ({
      ...prev,
      userGrowthStage: newStage,
      lastActivity: Date.now(),
    }));
  }, []);

  // === アクセシビリティ設定更新 ===
  const updateAccessibility = useCallback(
    (setting, value) => {
      setAccessibilitySettings((prev) => ({
        ...prev,
        [setting]: value,
      }));

      // ルート要素にクラス適用
      document.documentElement.className =
        document.documentElement.className.replace(/forest-\w+/g, '') +
        ` forest-text-${value === ACCESSIBILITY_OPTIONS.textSize.large ? 'large' : 'normal'}` +
        ` forest-contrast-${accessibilitySettings.contrast}` +
        ` forest-motion-${accessibilitySettings.motion}`;
    },
    [accessibilitySettings]
  );

  // === 認知負荷設定更新 ===
  const updateCognitive = useCallback((setting, value) => {
    setCognitiveSettings((prev) => ({
      ...prev,
      [setting]: value,
    }));
  }, []);

  // useContextualUX は外部フックに移動済み

  // === 初回訪問処理 ===
  useEffect(() => {
    const hasVisited = localStorage.getItem('forest_visited');
    if (!hasVisited) {
      setForestState((prev) => ({ ...prev, isFirstVisit: true }));
      setCognitiveSettings((prev) => ({ ...prev, showOnboarding: true }));
    } else {
      setForestState((prev) => ({ ...prev, isFirstVisit: false }));
      setCognitiveSettings((prev) => ({ ...prev, showOnboarding: false }));
    }
  }, []);

  const markAsVisited = useCallback(() => {
    localStorage.setItem('forest_visited', 'true');
    setForestState((prev) => ({ ...prev, isFirstVisit: false }));
    setCognitiveSettings((prev) => ({ ...prev, showOnboarding: false }));
  }, []);

  // === プロバイダー値 ===
  const value = {
    // 状態
    forestState,
    accessibilitySettings,
    cognitiveSettings,
    contextualData,

    // 更新関数
    setForestState,
    updateGrowthStage,
    updateAccessibility,
    updateCognitive,
    markAsVisited,

    // フックは外部でインポート

    // 定数
    GROWTH_STAGES,
    FOREST_TIME_STATES,
    ACCESSIBILITY_OPTIONS,

    // レスポンシブ情報
    isMobile,
    isTablet,
    isDesktop,
    isTouchDevice,
  };

  return (
    <ForestContext.Provider value={value}>{children}</ForestContext.Provider>
  );
}

export function useForest() {
  const context = useContext(ForestContext);
  if (!context) {
    throw new Error('useForest must be used within a ForestProvider');
  }
  return context;
}

export default ForestContext;
